/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// Dropping gatsby-plugin-offline stops new service workers being installed,
// but every device that already visited the site still has one registered, and
// it will go on serving its cached app shell indefinitely. This tears down
// whatever is there and empties its caches, so the next load comes from the
// network. Safe to keep permanently: with no plugin registering a worker,
// there is normally nothing to find.
export const onClientEntry = () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker
    .getRegistrations()
    .then(registrations => Promise.all(registrations.map(r => r.unregister())))
    .catch(() => {});

  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then(keys => Promise.all(keys.map(key => caches.delete(key))))
      .catch(() => {});
  }
};

let routeCount = 0;

// Scroll offset per pathname, remembered for the lifetime of the tab. Gatsby's
// own saved positions are keyed by history entry, so a forward navigation back
// to "/" (a "← Back" link rather than the browser button) never finds them.
const scrollMemory = {};

// Tracked here rather than read from onPreRouteUpdate's arguments: in this
// Gatsby version those arrive with `location` and `prevLocation` inverted, so
// keying off them stored each offset under the wrong path.
let currentPath = null;

const normalise = pathname => (pathname || '/').replace(/\/+$/, '') || '/';

export const onPreRouteUpdate = () => {
  // Fires before the next route paints, so window.scrollY still belongs to
  // whichever path we are leaving.
  if (currentPath !== null) {
    scrollMemory[currentPath] = window.scrollY;
  }
};

export const onRouteUpdate = () => {
  currentPath = normalise(window.location.pathname);

  routeCount += 1;
  if (routeCount > 1) {
    // Stops entrance animations (see src/utils/sr.js) replaying on pages
    // already visited this session.
    window.__hasNavigated = true;
  }

  // Anchor scrolling lives here rather than in a component effect: a hash-only
  // change keeps the same route, so Gatsby never re-renders the page and the
  // `location` prop a component sees stays stale. onRouteUpdate still fires.
  const { hash } = window.location;
  if (hash) {
    const id = decodeURIComponent(hash.slice(1));
    window.requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }
};

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  // Anchor targets are handled in onRouteUpdate above.
  if (location.hash) {
    return false;
  }

  const saved = scrollMemory[normalise(location.pathname)];

  // `behavior: 'instant'` overrides the CSS `scroll-behavior: smooth` that
  // anchor navigation relies on. Returning to a page should land exactly where
  // you left it, and a page you have not seen should open at the top — in both
  // cases with no visible travel. Returning [0, 0] here instead would let
  // Gatsby's own scrollTo inherit the smooth behaviour, which is what made a
  // new page render at the previous page's offset and then glide upward.
  const top = typeof saved === 'number' ? saved : 0;

  // Synchronously first: this runs in the commit phase, before the browser
  // paints, so a page you have not seen never shows itself at the offset you
  // were at on the previous one. Then again on the next frame, because a
  // restored position can sit below whatever height the document had at this
  // instant — the second call is a no-op when the target is the top.
  window.scrollTo({ top, left: 0, behavior: 'instant' });
  window.requestAnimationFrame(() => {
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  });

  return false;
};
